import * as React from 'react';
import { useContext } from 'react';

export type OrgScopedImpersonationContextType = {
  isAdmin: boolean;
  isPreviewMode: boolean;
  previewProgramIds: string[];
  previewProgramNames: string[];
  setPreviewMode: (programIds: string[]) => Promise<void>;
  exitPreviewMode: () => Promise<void>;
  resolvedProgramIds: string[];
  isResolvedProgramIdsLoading: boolean;
};

const OrgScopedImpersonationContext = React.createContext<OrgScopedImpersonationContextType | undefined>(undefined);

/**
 * @description Hook to check if the user is an admin for the org
 */
export const useIsOrgAdmin = () => {
  const context = useContext(OrgScopedImpersonationContext);
  return context?.isAdmin ?? false;
};

/**
 * @description Hook to check if org-scoped admin preview mode is active
 */
export const useIsOrgScopedPreviewMode = () => {
  const context = useContext(OrgScopedImpersonationContext);
  return context?.isPreviewMode ?? false;
};

/**
 * @description Hook to get the program IDs selected for preview
 */
export const usePreviewProgramIds = () => {
  const context = useContext(OrgScopedImpersonationContext);
  return context?.previewProgramIds ?? [];
};

/**
 * @description Hook to get the program names for the active preview session
 */
export const usePreviewProgramNames = () => {
  const context = useContext(OrgScopedImpersonationContext);
  return context?.previewProgramNames ?? [];
};

/**
 * @description Hook to get the function to exit preview mode
 */
export const useExitPreviewMode = () => {
  const context = useContext(OrgScopedImpersonationContext);
  return context?.exitPreviewMode;
};

/**
 * @description Hook to get the function to set/update preview mode programs
 */
export const useSetPreviewMode = () => {
  const context = useContext(OrgScopedImpersonationContext);
  return context?.setPreviewMode;
};

// Stable reference for the empty case so consumers don't get a new array identity on every render
// (which would defeat downstream memoization) when the hook is used outside the provider.
const EMPTY_PROGRAM_IDS: string[] = [];

/**
 * @description Hook to get the program IDs resolved for the current org via the Program Resolver
 * API. The resolver scopes program IDs to the org server-side and already accounts for the user
 * type (standard / admin impersonation / logged-out), so this is the canonical source of program
 * IDs for org-scoped home consumers. The data is fetched once by `OrgScopedImpersonationDataProvider`
 * and shared via context to avoid redundant requests.
 */
export const useResolvedProgramIds = () => {
  const context = useContext(OrgScopedImpersonationContext);
  return {
    programIds: context?.resolvedProgramIds ?? EMPTY_PROGRAM_IDS,
    loading: context?.isResolvedProgramIdsLoading ?? false,
  };
};

export default OrgScopedImpersonationContext;
